import React, { useState, useEffect } from 'react';
import { Loader2, Trash2, Globe, Lock } from 'lucide-react';
import { supabase } from '../../lib/supabase';

// Components
import ImageUploadSection from './ImageUploadSection';
import CameraSection from './CameraSection';
import AIResultsSection from './AIResultsSection';
import ImagePreviewModal from './ImagePreviewModal';
import AIResultsCompareModal from './AIResultsCompareModal';
import SlugInputSection from './SlugInputSection';

// Hooks
import { useSlugValidation } from '../../hooks/portfolio/useSlugValidation';
import { useCamera } from '../../hooks/portfolio/useCamera';

// Utils
import { enhanceImageWithAI } from '../../utils/portfolio/aiEnhancement';
import { applyBackgroundColor, applyGrayscaleFilter } from '../../utils/portfolio/imageUtils';
import { 
  saveAiResultsToStorage, 
  loadAiResultsFromStorage, 
  clearStoredResults 
} from '../../utils/portfolio/storageUtils';

const SlugInputModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  defaultSlug, 
  isLoading, 
  isEditMode, 
  userId, 
  sessionId,
  onDelete,
  existingPortfolio 
}) => {
  const [slug, setSlug] = useState(defaultSlug || '');
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [enhancementError, setEnhancementError] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImageSrc, setModalImageSrc] = useState(null); // 일반 이미지 미리보기용
  const [showAICompareModal, setShowAICompareModal] = useState(false); // AI 결과 비교 모달용
  const [compareModalInitialType, setCompareModalInitialType] = useState(null);
  const [isLoadingExistingImage, setIsLoadingExistingImage] = useState(false);
  const [hasNewImage, setHasNewImage] = useState(false); // 새 이미지 업로드 여부 추적
  const [enhancementProgress, setEnhancementProgress] = useState({ percent: 0, message: '' }); // AI 개선 진행률
  
  // AI 개선 결과 상태
  const [aiResults, setAiResults] = useState(null);
  const [selectedImageType, setSelectedImageType] = useState('original'); // 기본값을 original로 변경
  
  // 배경 색상 선택 상태
  const [showBackgroundColorPicker, setShowBackgroundColorPicker] = useState(false);
  const [selectedBackgroundColor, setSelectedBackgroundColor] = useState('#ffffff');
  const [backgroundRemovedImageWithColor, setBackgroundRemovedImageWithColor] = useState(null);
  const [colorChangeTimeout, setColorChangeTimeout] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false); // 삭제 확인 모달 상태
  const [isDeleting, setIsDeleting] = useState(false); // 삭제 중 상태
  const [isPublished, setIsPublished] = useState(false); // 공개/비공개 상태

  // 커스텀 훅들
  const { isCheckingSlug, slugAvailable, slugError } = useSlugValidation(slug, isEditMode);
  const { 
    isCameraOpen, 
    setIsCameraOpen, 
    isVideoReady, 
    cameraStream,
    videoRef, 
    canvasRef, 
    startCamera, 
    capturePhoto, 
    stopCameraStream 
  } = useCamera();

  // URL에서 파일 객체 생성 함수
  const createFileFromUrl = async (url, filename = 'existing-profile-image.jpg') => {
    try {
      console.log('이미지 로드 시도:', url);
      
      const response = await fetch(url);
      if (!response.ok) {
        console.error('이미지 로드 실패:', response.status, response.statusText);
        throw new Error(`Failed to fetch image: ${response.status}`);
      }
      
      const blob = await response.blob();
      const file = new File([blob], filename, { type: blob.type });
      console.log('이미지 로드 성공:', filename, file.size);
      return file;
    } catch (error) {
      console.error('이미지 로드 오류:', error);
      return null;
    }
  };

  // 백그라운드 AI 이미지 복원 함수
  const restoreAIImagesInBackground = async (portfolioData) => {
    try {
      const aiData = portfolioData.ai_enhancement_data;
      if (!aiData) {
        console.warn('AI 데이터가 없습니다');
        return;
      }
      
      // 이미지 복원을 청크 단위로 처리 (2개씩)
      const imageUrls = [
        { key: 'originalFile', url: portfolioData.original_image_url, name: 'original.jpg' },
        { key: 'gfpganFile', url: aiData.gfpgan_url, name: 'gfpgan.jpg' },
        { key: 'gfpganBackgroundRemoverFile', url: aiData.background_remover_url, name: 'bg-remover.jpg' },
        { key: 'gfpganBackgroundRemoverBlackWhiteFile', url: aiData.black_white_url, name: 'bw.jpg' },
        { key: 'finalFile', url: aiData.final_url, name: 'final.jpg' }
      ].filter(item => item.url);
      
      console.log('AI 이미지 복원 시작:', imageUrls.length + '개 이미지');
      
      // 청크 단위로 순차 처리하여 브라우저 부하 감소
      const chunkSize = 2;
      const restoredResults = {};
      
      for (let i = 0; i < imageUrls.length; i += chunkSize) {
        const chunk = imageUrls.slice(i, i + chunkSize);
        const chunkPromises = chunk.map(async ({ key, url, name }) => {
          try {
            const file = await createFileFromUrl(url, name);
            return { key, file };
          } catch (error) {
            console.warn(`이미지 복원 실패 (${key}):`, error.message);
            return { key, file: null };
          }
        });
        
        const chunkResults = await Promise.allSettled(chunkPromises);
        chunkResults.forEach(result => {
          if (result.status === 'fulfilled' && result.value.file) {
            restoredResults[result.value.key] = result.value.file;
          }
        });
      }
      
      console.log('AI 이미지 복원 완료:', Object.keys(restoredResults).length + '개 성공');
      
      // AI 결과 설정
      if (Object.keys(restoredResults).length > 0) {
        setAiResults(restoredResults);
        
        // 배경색상 복원 (지연 처리)
        if (aiData.selected_background_color && aiData.selected_type === 'gfpgan-bg-remover') {
          setSelectedBackgroundColor(aiData.selected_background_color);
          setShowBackgroundColorPicker(true);
          
          // 배경색상 적용을 더 나중에 처리
          setTimeout(async () => {
            if (restoredResults.gfpganBackgroundRemoverFile) {
              try {
                const coloredImage = await applyBackgroundColor(
                  restoredResults.gfpganBackgroundRemoverFile, 
                  aiData.selected_background_color
                );
                setBackgroundRemovedImageWithColor(coloredImage);
              } catch (error) {
                console.warn('배경색상 적용 실패:', error.message);
              }
            }
          }, 500);
        }
      } else {
        console.warn('복원된 AI 이미지가 없습니다');
      }
    } catch (error) {
      console.error('백그라운드 AI 이미지 복원 오류:', error);
    }
  };

  // 기존 포트폴리오 이미지 로드 함수 (최적화)
  const loadExistingPortfolioImage = async () => {
  if (!isEditMode || !sessionId) return;
  
  setIsLoadingExistingImage(true);
  
  try {
  const { data: portfolioData, error } = await supabase
  .from('portfolios')
  .select('profile_image_url, original_image_url, ai_enhancement_data')
  .eq('session_id', sessionId)
  .maybeSingle();
  
  if (error || !portfolioData) {
    console.error('포트폴리오 데이터 로드 실패:', error);
        return;
  }
    
  // 핵심 개선: AI 데이터가 있을 때만 복원 작업 수행
  // AI 데이터가 null이면 새 이미지로 교체된 상태임
  if (portfolioData.ai_enhancement_data) {
  // 즉시 UI 업데이트를 위해 기본 이미지부터 설정
  const quickImageUrl = portfolioData.profile_image_url || portfolioData.original_image_url;
  if (quickImageUrl) {
    setImagePreview(quickImageUrl);
    setSelectedImageType(portfolioData.ai_enhancement_data.selected_type || 'original');
  }
  
  // 성능 개선: AI 이미지 복원을 백그라운드에서 처리
  setTimeout(() => {
      restoreAIImagesInBackground(portfolioData).catch(err => {
      console.warn('AI 이미지 복원 실패:', err);
    });
  }, 0);
  
  } else {
  // AI 데이터가 없으면 기본 이미지만 표시
  const imageToShow = portfolioData.original_image_url || portfolioData.profile_image_url;
  if (imageToShow) {
  // 이미지 유효성 확인을 위한 임시 이미지 객체 생성
  const testImg = new Image();
  testImg.onload = () => {
    // 이미지가 성공적으로 로드되면 미리보기 설정
    setImagePreview(imageToShow);
  
  // 파일 객체 생성을 백그라운드에서 처리
  setTimeout(async () => {
  try {
  const file = await createFileFromUrl(imageToShow, 'existing-image.jpg');
    if (file) {
    setProfileImageFile(file);
      console.log('기존 이미지 파일 생성 성공');
      } else {
      console.warn('기존 이미지 파일 생성 실패 - 이미지가 존재하지 않을 수 있습니다');
      }
      } catch (error) {
          console.error('기존 이미지 파일 생성 오류:', error);
        }
            }, 0);
          };
          
          testImg.onerror = () => {
            console.warn('기존 이미지를 로드할 수 없습니다:', imageToShow);
            // 이미지 로드 실패 시 미리보기 설정하지 않음
          };
          
          testImg.src = imageToShow;
        }
        setSelectedImageType('original');
      }
      
    } catch (error) {
      console.error('포트폴리오 이미지 로드 오류:', error);
    } finally {
      setIsLoadingExistingImage(false); // 즉시 로딩 완료 처리
    }
  };

  // 초기화
  useEffect(() => {
    if (isOpen) {
      setSlug(defaultSlug || '');
      setIsCameraOpen(false);
      setIsEnhancing(false);
      setEnhancementError(null);
      setShowImageModal(false);
      setModalImageSrc(null);
      setShowAICompareModal(false);
      setCompareModalInitialType(null);
      setShowBackgroundColorPicker(false);
      setSelectedBackgroundColor('#ffffff');
      setBackgroundRemovedImageWithColor(null);
      setHasNewImage(false); // 모달 열릴 때 초기화
      
      // 기존 포트폴리오의 공개 상태 설정
      if (isEditMode && existingPortfolio) {
        setIsPublished(existingPortfolio.status === 'published');
      } else {
        setIsPublished(false); // 기본값은 비공개
      }
      
      // 수정 모드가 아닐 때만 이미지 상태 초기화
      if (!isEditMode) {
        setProfileImageFile(null);
        setImagePreview(null);
        setAiResults(null);
        setSelectedImageType('original'); // 기본값을 original로 변경
        
        // 저장된 AI 결과 복원 시도 (새 생성 모드)
        loadAiResultsFromStorage(userId, sessionId).then(async restoredResults => {
          if (restoredResults) {
            setAiResults(restoredResults);
            const restoredType = restoredResults.selectedImageType || 'original';
            setSelectedImageType(restoredType);
            
            // 배경색상 복원 및 이미지 재생성
            if (restoredResults.selectedBackgroundColor) {
              setSelectedBackgroundColor(restoredResults.selectedBackgroundColor);
              
              // 배경제거 타입이고 배경색상이 있으면 색상 적용된 이미지 재생성
              if (restoredType === 'gfpgan-bg-remover' && restoredResults.gfpganBackgroundRemoverFile) {
                setShowBackgroundColorPicker(true);
                try {
                  // 저장된 배경색상으로 이미지 재생성 (비동기 처리)
                  applyBackgroundColor(restoredResults.gfpganBackgroundRemoverFile, restoredResults.selectedBackgroundColor)
                    .then(coloredImage => {
                      setBackgroundRemovedImageWithColor(coloredImage);
                    })
                    .catch(error => {
                      // 배경 색상 적용 실패 시 조용히 무시
                    });
                } catch (error) {
                  // 오류 발생 시 조용히 무시
                }
              }
            }
            
            // 선택된 타입에 따라 프로필 이미지 설정
            updateProfileImageBySelection(restoredResults, restoredType);
          }
        }).catch(error => {
          // 저장된 AI 결과 로드 실패 시 조용히 무시
        });
      } else {
      // 수정 모드일 때는 기존 포트폴리오 이미지 로드
      loadExistingPortfolioImage();
        }
    } else {
      stopCameraStream();
    }
  }, [isOpen, defaultSlug, isEditMode, userId, sessionId]);
  
  // AI 결과가 설정된 후 흑백 이미지 생성
  useEffect(() => {
    const generateMissingBlackWhiteImage = async () => {
      if (aiResults && aiResults.gfpganBackgroundRemoverFile && !aiResults.gfpganBackgroundRemoverBlackWhiteFile) {
        try {
          const grayBackgroundImage = await applyBackgroundColor(aiResults.gfpganBackgroundRemoverFile, '#9ca3af');
          const blackWhiteImage = await applyGrayscaleFilter(grayBackgroundImage);
          
          setAiResults(prev => ({
            ...prev,
            gfpganBackgroundRemoverBlackWhiteFile: blackWhiteImage
          }));
        } catch (error) {
          console.error('흑백 이미지 생성 실패:', error);
        }
      }
    };
    
    generateMissingBlackWhiteImage();
  }, [aiResults?.gfpganBackgroundRemoverFile]);

  // 배경 색상이 변경될 때 현재 선택이 배경제거라면 상단 이미지도 업데이트
  useEffect(() => {
    // 현재 선택된 타입이 배경제거이고, 배경색이 적용된 이미지가 있을 때만 업데이트
    if (selectedImageType === 'gfpgan-bg-remover' && backgroundRemovedImageWithColor) {
      setProfileImageFile(backgroundRemovedImageWithColor);
      const newPreviewUrl = URL.createObjectURL(backgroundRemovedImageWithColor);
      // 이전 blob URL 정리
      if (imagePreview && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }
      setImagePreview(newPreviewUrl);
    }
  }, [backgroundRemovedImageWithColor]); // selectedImageType은 의존성에서 제거
  
  // 메모리 관리 개선
  useEffect(() => {
    return () => {
      // 컴포넌트 언마운트 시 타임아웃 정리
      if (colorChangeTimeout) {
        clearTimeout(colorChangeTimeout);
      }
      
      // URL 객체 정리
      if (imagePreview && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, []);

  // ESC 키 이벤트 처리
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        if (showAICompareModal) {
          setShowAICompareModal(false);
        } else if (showImageModal) {
          setShowImageModal(false);
        }
      }
    };
    
    if (showImageModal || showAICompareModal) {
      document.addEventListener('keydown', handleEscKey);
      return () => document.removeEventListener('keydown', handleEscKey);
    }
  }, [showImageModal, showAICompareModal]);

  // 선택된 이미지 타입에 따라 상단 프로필 이미지 업데이트
  const updateProfileImageBySelection = (aiResultsData, type) => {
    if (!aiResultsData) return;
        
    let selectedFile = null;
    
    switch (type) {
      case 'original':
        selectedFile = aiResultsData.originalFile;
        break;
      case 'gfpgan':
        selectedFile = aiResultsData.gfpganFile;
        break;
      case 'gfpgan-bg-remover':
        // 배경 색상이 적용된 이미지가 있으면 그것을 사용, 없으면 기본 배경제거 이미지
        selectedFile = backgroundRemovedImageWithColor || aiResultsData.gfpganBackgroundRemoverFile;
        break;
      case 'gfpgan-bg-remover-bw':
        selectedFile = aiResultsData.gfpganBackgroundRemoverBlackWhiteFile;
        console.log('흑백 이미지 선택:', {
          hasFile: !!selectedFile,
          fileSize: selectedFile?.size,
          fileType: selectedFile?.type
        });
        break;
      case 'final':
        selectedFile = aiResultsData.finalFile;
        break;
      default:
        selectedFile = aiResultsData.originalFile;
    }
    
    if (selectedFile) {
      setProfileImageFile(selectedFile);
      // 이전 blob URL 정리
      if (imagePreview && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }
      setImagePreview(URL.createObjectURL(selectedFile));
    } else {
      console.warn('선택된 파일이 없습니다:', type);
    }
  };

  // 이벤트 핸들러들
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      setIsCameraOpen(false);
      stopCameraStream();
      
      // 새 이미지 업로드 플래그 설정
      setHasNewImage(true);
      
      // 새 이미지 업로드 시 현재 세션의 AI 결과만 초기화 (localStorage는 유지)
      setAiResults(null);
      setSelectedImageType('original');
      setShowBackgroundColorPicker(false);
      setSelectedBackgroundColor('#ffffff');
      setBackgroundRemovedImageWithColor(null);
      
      // localStorage는 수정 완료 시점에 초기화하도록 변경
      console.log('✅ 새 이미지 업로드: 현재 AI 결과만 초기화 (localStorage는 유지)');
    }
  };

  const handleTakePhotoClick = async () => {
    // 카메라 상태를 완전히 리셋
    stopCameraStream();
    setIsCameraOpen(false);
    
    // 잠시 대기 후 새로 시작
    setTimeout(async () => {
      try {
        await startCamera();
      } catch (error) {
        alert('카메라 시작에 실패했습니다: ' + error.message);
      }
    }, 1000);
  };

  const handleCapture = () => {
    capturePhoto((file) => {
      setProfileImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      
      // 새 사진 촬영 플래그 설정
      setHasNewImage(true);
      
      // 새 사진 촬영 시 현재 세션의 AI 결과만 초기화 (localStorage는 유지)
      setAiResults(null);
      setSelectedImageType('original');
      setShowBackgroundColorPicker(false);
      setSelectedBackgroundColor('#ffffff');
      setBackgroundRemovedImageWithColor(null);
      
    });
  };

  const handleEnhanceImage = async () => {
    if (!profileImageFile) {
      alert('이미지를 먼저 업로드해주세요.');
      return;
    }

    setIsEnhancing(true);
    setEnhancementError(null);
    setEnhancementProgress({ percent: 0, message: '' });
    
    // AI 개선 시작 전에 기존 AI 결과 초기화
    setAiResults(null);
    setShowBackgroundColorPicker(false);
    setSelectedBackgroundColor('#ffffff');
    setBackgroundRemovedImageWithColor(null);
    clearStoredResults(userId, sessionId);

    try {
      const result = await enhanceImageWithAI(profileImageFile, (progress) => {
        setEnhancementProgress(progress);
      });
      
      setAiResults(result);
      
      if (result.gfpganBackgroundRemoverFile) {
        try {
          const grayBackgroundImage = await applyBackgroundColor(result.gfpganBackgroundRemoverFile, '#9ca3af');
          const grayscaleBackgroundRemoverImage = await applyGrayscaleFilter(grayBackgroundImage);
          result.gfpganBackgroundRemoverBlackWhiteFile = grayscaleBackgroundRemoverImage;
          setAiResults(result);
        } catch (error) {
          // 회색조 이미지 생성 실패 시 조용히 무시
        }
      }
      
      await saveAiResultsToStorage(result, userId, sessionId, selectedImageType, selectedBackgroundColor);
      
      // AI 개선 후 기본 선택은 원본 유지 (사용자가 선택하도록)
      // setSelectedImageType은 그대로 두고 상단 이미지도 현재 선택에 따라 유지
      
    } catch (error) {
      setEnhancementError(error.message);
    } finally {
      setIsEnhancing(false);
      setEnhancementProgress({ percent: 0, message: '' });
    }
  };
  
  const handleBackgroundColorChange = async (color) => {
    setSelectedBackgroundColor(color);
    
    // 디바운싱: 사용자가 색상 변경을 멈춘 후 300ms 뒤에 실제 처리
    if (colorChangeTimeout) {
      clearTimeout(colorChangeTimeout);
    }
    
    const newTimeout = setTimeout(async () => {
      if (aiResults && aiResults.gfpganBackgroundRemoverFile) {
        try {
          const coloredImage = await applyBackgroundColor(
            aiResults.gfpganBackgroundRemoverFile, 
            color
          );
          setBackgroundRemovedImageWithColor(coloredImage);
        } catch (error) {
          // 조용히 무시
        }
      }
    }, 300);
    
    setColorChangeTimeout(newTimeout);
  };

  // AI 개선 결과 선택 핸들러 - 상단 프로필 사진도 함께 변경 (최적화)
  const handleImageTypeSelect = (type) => {
    if (!aiResults) return;
    
    // 즉시 UI 반응을 위해 상태 먼저 업데이트
    setSelectedImageType(type);
    
    // 실제 이미지 교체는 다음 틱에서 처리
    requestAnimationFrame(() => {
      updateProfileImageBySelection(aiResults, type);
      
      if (type === 'gfpgan-bg-remover') {
        setShowBackgroundColorPicker(true);
      } else {
        setShowBackgroundColorPicker(false);
        // 배경제거가 아닌 다른 타입을 선택할 때만 배경색 이미지 초기화
        setBackgroundRemovedImageWithColor(null);
      }
    });
    
    // 저장은 비동기로 처리
    setTimeout(() => {
      if (aiResults) {
        saveAiResultsToStorage({
          ...aiResults,
          selectedImageType: type
        }, userId, sessionId, type, selectedBackgroundColor);
      }
    }, 0);
  };

  // 일반 이미지 미리보기 (상단 프로필 사진용)
  const handleImagePreview = (imageSrc) => {
    setModalImageSrc(imageSrc);
    setShowImageModal(true);
  };

  // AI 결과 비교 모달 열기
  const handleAIResultsCompare = (aiResultsData, initialType) => {
    if (!aiResultsData) {
      return;
    }
    
    setCompareModalInitialType(initialType);
    setShowAICompareModal(true);
  };

  const handleImageDelete = () => {
    setProfileImageFile(null);
    setImagePreview(null);
    setShowImageModal(false);
    setShowAICompareModal(false);
    setAiResults(null);
    setSelectedImageType('original');
    setShowBackgroundColorPicker(false);
    setSelectedBackgroundColor('#ffffff');
    setBackgroundRemovedImageWithColor(null);
    
    // 이미지 삭제도 "새로운 변경"으로 처리
    setHasNewImage(true);
    
    // localStorage에서 AI 결과 삭제 (이미지를 아예 삭제하는 경우)
    clearStoredResults(userId, sessionId);    
  };

  const handleConfirm = () => {
    if (!isEditMode && (!slugAvailable || slugError)) {
      return;
    }
    
    // 현재 선택된 이미지(상단에 표시된 이미지)와 선택된 타입, 배경색상, 공개 상태를 함께 전달
    // 이미지가 없어도 null을 전달하여 처리 가능하도록 함
    onConfirm(slug, profileImageFile, aiResults, selectedImageType, selectedBackgroundColor, hasNewImage, isPublished);
  };

  // 포트폴리오 삭제 핸들러
  const handleDelete = async () => {
    if (!sessionId) return;
    
    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from('portfolios')
        .update({ deleted_at: new Date().toISOString() })
        .eq('session_id', sessionId)
        .eq('user_id', userId);
        
      if (error) {
        console.error('포트폴리오 삭제 오류:', error);
        alert('포트폴리오 삭제 중 오류가 발생했습니다.');
      } else {
        // alert 제거 - 바로 닫기 처리
        if (onDelete) {
          onDelete();
        } else {
          onClose();
        }
      }
    } catch (error) {
      console.error('포트폴리오 삭제 중 예상치 못한 오류:', error);
      alert('포트폴리오 삭제 중 오류가 발생했습니다.');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: '30rem', cursor: 'default', padding: '1.25rem', maxHeight: '85vh', overflow: 'visible' }}>
        <h3 className="text-lg font-semibold text-gray-900 mb-3">
          {isEditMode ? '내 웹페이지 수정' : '내 웹페이지 만들기'}
        </h3>
        
        {/* 스크롤 가능한 콘텐츠 영역 */}
        <div className="flex-1 overflow-y-auto" style={{ overflowX: 'visible', paddingRight: '4px' }}>
          {/* 기존 이미지 로딩 상태 */}
          {isLoadingExistingImage && (
            <div className="alert alert-info mb-4">
              <div className="flex items-center">
                <Loader2 className="animate-spin mr-2 h-4 w-4" />
                <span className="alert-description">기존 웹 페이지 이미지를 불러오는 중...</span>
              </div>
            </div>
          )}
          
          {/* 포트폴리오 주소 설정 - 최상단으로 이동 */}
          <SlugInputSection
            slug={slug}
            setSlug={setSlug}
            isEditMode={isEditMode}
            isCheckingSlug={isCheckingSlug}
            slugAvailable={slugAvailable}
            slugError={slugError}
          />
          
          {/* 공개 설정 토글 - 항상 표시 (수정 모드에서도) */}
          <div className="mb-3 -mt-8">
            <div className="bg-gray-50 rounded-lg p-2.5">
              <div className="flex items-center justify-between">
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                  {isPublished ? (
                    <Globe className="text-green-600 flex-shrink-0" size={16} />
                  ) : (
                    <Lock className="text-gray-400 flex-shrink-0" size={16} />
                  )}
                  <span>공개 설정</span>
                  <span className="text-xs font-normal text-gray-600 ml-1">
                    {isPublished ? '누구나 접근 가능' : '본인만 확인 가능'}
                  </span>
                </label>
                <button
                  type="button"
                  onClick={() => setIsPublished(!isPublished)}
                  className={`relative inline-flex h-5 w-10 flex-shrink-0 items-center rounded-full transition-colors ${
                    isPublished ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                      isPublished ? 'translate-x-5' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
          
          {/* 프로필 사진 업로드 & 촬영 */}
          <ImageUploadSection
            imagePreview={imagePreview}
            onImageChange={handleImageChange}
            onTakePhoto={handleTakePhotoClick}
            onEnhanceImage={handleEnhanceImage}
            onImageDelete={handleImageDelete}
            profileImageFile={profileImageFile}
            isEnhancing={isEnhancing}
            isLoadingExistingImage={isLoadingExistingImage}
            isCameraOpen={isCameraOpen}
            aiResults={aiResults}
            enhancementProgress={enhancementProgress}
          />
          
          {/* 카메라 뷰 */}
          <CameraSection
            isCameraOpen={isCameraOpen}
            isVideoReady={isVideoReady}
            videoRef={videoRef}
            canvasRef={canvasRef}
            cameraStream={cameraStream}
            onCancel={() => { setIsCameraOpen(false); stopCameraStream(); }}
            onCapture={handleCapture}
            onRestart={handleTakePhotoClick}
          />
            
          {/* AI 개선 에러 메시지 */}
          {enhancementError && (
            <div className="alert alert-danger mt-2">
              <div className="alert-description">
                AI 개선 오류: {enhancementError}
              </div>
            </div>
          )}
          
          {/* 사진 스타일 선택 */}
          <AIResultsSection
            aiResults={aiResults}
            selectedImageType={selectedImageType}
            onImageTypeSelect={handleImageTypeSelect}
            showBackgroundColorPicker={showBackgroundColorPicker}
            selectedBackgroundColor={selectedBackgroundColor}
            onBackgroundColorChange={handleBackgroundColorChange}
            backgroundRemovedImageWithColor={backgroundRemovedImageWithColor}
            onImagePreview={handleAIResultsCompare}
            profileImageFile={profileImageFile}
          />
          
          {/* 일반 이미지 크게 보기 모달 (상단 프로필 사진용) */}
          <ImagePreviewModal
            isOpen={showImageModal}
            imagePreview={modalImageSrc}
            onClose={() => {
              setShowImageModal(false);
              setModalImageSrc(null);
            }}
          />

          {/* AI 결과 비교 모달 */}
          <AIResultsCompareModal
            isOpen={showAICompareModal}
            aiResults={aiResults}
            initialImageType={compareModalInitialType}
            backgroundRemovedImageWithColor={backgroundRemovedImageWithColor}
            onClose={() => {
              setShowAICompareModal(false);
              setCompareModalInitialType(null);
            }}
          />
        </div>
        
        {/* 고정된 하단 버튼 영역 */}
        <div className="flex justify-between mt-3 pt-3 border-t">
          {/* 삭제 버튼 (수정 모드일 때만 표시) - 휴지통 아이콘 */}
          {isEditMode && (
            <button 
              onClick={() => setShowDeleteConfirm(true)} 
              disabled={isLoading || isEnhancing || isLoadingExistingImage} 
              className="btn btn-ghost btn-md text-gray-500 hover:text-red-600 hover:bg-red-50"
              title="웹페이지 삭제"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          )}
          
          {/* 오른쪽 버튼들 */}
          <div className="flex gap-2 ml-auto">
            <button 
              onClick={onClose} 
              disabled={isLoading || isEnhancing || isLoadingExistingImage} 
              className="btn btn-secondary btn-md"
            >
              취소
            </button>
            <button 
              onClick={handleConfirm} 
              disabled={(!isEditMode && (!slugAvailable || slugError)) || isLoading || isEnhancing || isLoadingExistingImage} 
              className="btn btn-primary btn-md"
            >
              {(isLoading || isEnhancing || isLoadingExistingImage) && <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />}
              {isLoading ? '저장 중...' : isEnhancing ? 'AI 처리 중...' : isLoadingExistingImage ? '로딩 중...' : (isEditMode ? '수정 완료' : '웹페이지 만들기')}
            </button>
          </div>
        </div>
        
        {/* 삭제 확인 모달 */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-3">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  웹페이지 삭제
                </h3>
              </div>
              <p className="text-gray-600 mb-6 ml-15">
                정말로 이 웹페이지를 삭제하시겠습니까? 삭제된 웹페이지는 복구할 수 없습니다.
              </p>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={isDeleting}
                  className="btn btn-secondary btn-md"
                >
                  취소
                </button>
                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="btn btn-danger btn-md"
                >
                  {isDeleting && <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />}
                  {isDeleting ? '삭제 중...' : '삭제'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SlugInputModal;
